import {
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

/*
The tabs of a group are one contiguous run in the documents array. The bar renders
that array left to right, so ordering and grouping are the same thing.

A dragged tab belongs to the group whose span it landed inside, see getDroppedGroupId.
A tab only joins a group by dragging if it lands between any two tabs in the group.
 */

export const getDroppedGroupId = (
  ownGroupId: string | null,
  prevGroupId: string | null,
  nextGroupId: string | null
): string | null => {
  if (prevGroupId === nextGroupId) {
    return prevGroupId;
  }

  return ownGroupId === prevGroupId || ownGroupId === nextGroupId
    ? ownGroupId
    : null;
};

export const snapOutOfGroup = (
  documents: WorkspaceDocument[],
  index: number,
  forward: boolean
): number => {
  let snapped = index;

  while (
    documents[snapped - 1]?.groupId &&
    documents[snapped - 1]?.groupId === documents[snapped]?.groupId
  ) {
    snapped += forward ? 1 : -1;
  }

  return snapped;
};

// index just past the group's last tab or `fallback` when the group has no tabs
export const getGroupEndIndex = (
  documents: WorkspaceDocument[],
  groupId: string | null,
  fallback: number
): number => {
  const lastIndex = documents
    .map(document => document.groupId)
    .lastIndexOf(groupId);

  return lastIndex === -1 ? fallback : lastIndex + 1;
};

export const insertAt = <T>(items: T[], index: number, inserted: T[]): T[] => [
  ...items.slice(0, index),
  ...inserted,
  ...items.slice(index),
];

// a collapsed group hides its tabs
export const isTabHidden = (
  tabGroups: TabGroup[],
  document: WorkspaceDocument
): boolean =>
  tabGroups.some(
    tabGroup => tabGroup.id === document.groupId && tabGroup.collapsed
  );

/*
  Get the nearest visible (ungrouped/uncollapsed) tab.
  Searches outward from `fromIndex` when the caller has a tab to search around,
  otherwise left to right.
  Returns null when no candidate is visible (the caller decides the fallback behaviour).
 */
export const getNearestVisibleTabId = (
  tabGroups: TabGroup[],
  documents: WorkspaceDocument[],
  fromIndex?: number
): string | null => {
  const candidates =
    fromIndex === undefined
      ? documents
      : [
          ...documents.slice(0, fromIndex).reverse(),
          ...documents.slice(fromIndex + 1),
        ];

  return (
    candidates.find(document => !isTabHidden(tabGroups, document))?.id ?? null
  );
};
